import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { clearAuthToken } from '@/lib/api';
import { Menu, LayoutGrid, Target, Settings, LogOut } from 'lucide-react';
import EditProfileModal from '@/components/EditProfileModal';
import PlayerDashboard from '@/pages/PlayerDashboard';

const C = {
  orange: '#F5A623', navy: '#0D1B26', navyCard: '#1A2B3C', navyBorder: '#243448',
  muted: '#8FA3B1', textSec: '#A8BFCE', border: '#DDE5EC', bg: '#F0F3F6',
};
const SIDEBAR_W = 220;

function NavItem({ icon, label, active, onClick }) {
  return (
    <button onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
      className={`relative flex items-center gap-2.5 w-full text-left px-5 py-2.5 text-[18px] font-bold uppercase tracking-[.06em] transition-colors ${
        active ? 'text-[#F5A623] bg-[rgba(245,166,35,.08)]' : 'text-[#A8BFCE] hover:text-white hover:bg-white/5'
      }`}
      style={{ fontFamily: "'Barlow Condensed', sans-serif", border: 'none', background: active ? 'rgba(245,166,35,.08)' : 'none' }}>
      {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r" style={{ background: C.orange }} />}
      {icon}{label}
    </button>
  );
}

function TabItem({ icon, label, active, onClick }) {
  return (
    <button onClick={onClick}
      data-testid={`tab-${label.toLowerCase()}`}
      className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${
        active ? 'text-[#F5A623]' : 'text-[#8FA3B1] hover:text-[#A8BFCE]'
      }`}
      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', background: 'none', border: 'none' }}>
      {icon}{label}
    </button>
  );
}

export default function PlayerLayout({ user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const navigate = useNavigate();
  const initials = (user?.name || 'P').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const handleLogout = async () => {
    try { await api.post('/auth/logout', {}); } catch {}
    clearAuthToken(); navigate('/login');
  };

  return (
    <div className="flex min-h-screen" style={{ fontFamily: "'Barlow', sans-serif", background: C.bg }}>
      {/* SIDEBAR */}
      <aside data-testid="sidebar"
        className={`fixed top-0 left-0 bottom-0 z-[200] flex flex-col transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0 shadow-[4px_0_24px_rgba(0,0,0,0.3)]' : '-translate-x-full'} md:translate-x-0 md:shadow-none`}
        style={{ width: SIDEBAR_W, background: C.navy }}>
        <div className="h-[60px] flex items-center px-5 shrink-0" style={{ borderBottom: `1px solid ${C.navyBorder}` }}>
          <span className="font-bold text-xl uppercase tracking-[.02em] text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Smarter<span style={{ color: C.orange }}>Hoops</span>
          </span>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          <NavItem icon={<LayoutGrid className="w-[17px] h-[17px] shrink-0" />} label="Dashboard" active={true}
            onClick={() => { setSidebarOpen(false); navigate('/dashboard'); }} />
          <NavItem icon={<Target className="w-[17px] h-[17px] shrink-0" />} label="Practice" active={false}
            onClick={() => { setSidebarOpen(false); navigate('/practice'); }} />
        </nav>
        <div className="py-4 pb-24 md:pb-4 shrink-0" style={{ borderTop: `1px solid ${C.navyBorder}` }}>
          <NavItem icon={<Settings className="w-[17px] h-[17px] shrink-0" />} label="Settings"
            onClick={() => { setSidebarOpen(false); setShowEditProfile(true); }} />
          <NavItem icon={<LogOut className="w-[17px] h-[17px] shrink-0" />} label="Sign Out" onClick={handleLogout} />
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-[190] bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* MAIN */}
      <div className="flex-1 flex flex-col min-w-0 md:ml-[220px]">
        <header className="h-[60px] bg-white flex items-center justify-between px-4 md:px-7 sticky top-0 z-[100] shrink-0"
          style={{ borderBottom: `1px solid ${C.border}` }} data-testid="topbar">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-1" onClick={() => setSidebarOpen(true)} data-testid="hamburger-menu"
              style={{ color: C.navy, background: 'none', border: 'none' }}>
              <Menu className="w-[22px] h-[22px]" />
            </button>
            <span className="md:hidden font-bold text-lg uppercase tracking-[.02em]" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: C.navy }}>
              Smarter<span style={{ color: C.orange }}>Hoops</span>
            </span>
          </div>
          <button onClick={() => setShowEditProfile(true)} data-testid="topbar-avatar"
            className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-xs font-extrabold tracking-[.04em] cursor-pointer"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", background: C.navyCard, color: C.orange, border: 'none' }}>
            {initials}
          </button>
        </header>

        <div
          className="p-5 md:p-7 overflow-y-auto flex-1 pb-app-nav"
          style={{
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)',
          }}
        >
          <PlayerDashboard user={currentUser} insideLayout />
          <div aria-hidden className="md:hidden" style={{ height: 'calc(env(safe-area-inset-bottom) + 130px)', flexShrink: 0 }} />
        </div>
      </div>

      {/* BOTTOM TABS */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 z-[300] md:hidden"
        style={{ background: C.navy, borderTop: `1px solid ${C.navyBorder}`, paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="bottom-tabs">
        <div className="flex h-full">
          <TabItem icon={<LayoutGrid className="w-5 h-5" />} label="Home" active={true} onClick={() => navigate('/dashboard')} />
          <TabItem icon={<Target className="w-5 h-5" />} label="Practice" active={false} onClick={() => navigate('/practice')} />
          <TabItem icon={<Settings className="w-5 h-5" />} label="Settings" active={false} onClick={() => setShowEditProfile(true)} />
        </div>
      </nav>

      <EditProfileModal open={showEditProfile} onOpenChange={setShowEditProfile} user={currentUser} onSuccess={(u) => setCurrentUser(u)} />
    </div>
  );
}
